/**
 * Errors that are safe to put on a screen.
 *
 * A driver says things like "UNIQUE constraint failed:
 * obligations.subscription_id", and a gateway says "Request failed with status
 * code 500" — useful in a log, unusable in front of a customer. Anything the UI
 * is meant to show is an AppError whose message is already the Spanish sentence
 * a person should read; everything else is caught at the screen edge and shown
 * generically, with the technical detail kept to the console.
 *
 * The server is where most of these now come from: `contracts/errors.ts`'
 * SerializedError carries `error.message` written for a person, and the HTTP
 * repository turns exactly that into an AppError. What is left here is the
 * translation layer both clients need and neither should own.
 */
export class AppError extends Error {
    // `override`, because ES2022's Error already declares `cause`. Redeclaring it
    // is still worth doing: this one is readonly, and the base's is not.
    cause;
    /**
     * WHETHER TRYING AGAIN COULD POSSIBLY WORK.
     *
     * The client already tells a contract 404 from a domain one, in
     * `isRouterNotFound` (see `http.ts`), and until this field existed it threw
     * the answer away: an `AppError` carried the sentence and nothing else. So a
     * screen had no way to stop offering «Reintentar» to somebody whose app is
     * simply newer than the deployed API, and that button repeats the same 404
     * forever. Carrying the answer is what lets a screen withhold a retry that
     * can never succeed.
     *
     * Retrying is the right answer to a connection that dropped and the wrong
     * one to a contract that does not line up: one heals by waiting, the other
     * only by deploying.
     *
     * TRUE BY DEFAULT, deliberately. Every other error this app raises is a
     * domain sentence a person can act on, and "try again" is a harmless thing
     * to offer next to it. Only what is KNOWN to be incurable says so.
     */
    retryable;
    /**
     * The server's `error.details`, when it sent any: what tells two refusals
     * with one code apart -- the assistant's month spent and a burst are both
     * a 429 `RATE_LIMITED`, and only `details.reason` says which.
     */
    details;
    constructor(message, cause, options = {}) {
        super(message);
        this.name = "AppError";
        this.cause = cause;
        this.retryable = options.retryable ?? true;
        this.details = options.details;
    }
}
/**
 * Whether a screen should offer to try again.
 *
 * Anything this app did not write — a dropped connection never reaches
 * `unwrap`'s translation, because there is no response and no body — is
 * assumed retryable: nothing is known about it except that waiting is the
 * only thing left to try.
 */
export function isRetryable(error) {
    return error instanceof AppError ? error.retryable : true;
}
/**
 * A failure that is the caller's to fix: a 4xx the server answered with a
 * sentence (over the balance, a plate already taken). Everything else is ours
 * and worth a report -- a 5xx, no answer at all, and the router's own 404 for
 * a route this build calls and the deployed API does not have (`retryable`
 * false: no retry and no user fixes it, only a deploy).
 */
export function isCallerError(error) {
    if (error instanceof AppError && !error.retryable)
        return false;
    const cause = error instanceof AppError ? error.cause : error;
    const status = cause?.response?.status;
    return typeof status === "number" && status >= 400 && status < 500;
}
/**
 * Whether a failed WRITE is worth a Sentry report: a 5xx, the router's own
 * 404, or anything that is not an HTTP failure at all -- a bug. Never the
 * caller's 4xx (`isCallerError`), and never a request that got no answer --
 * offline, DNS, a timeout. That is the person's connection, and where a
 * business's wifi is a phone hotspot it would bury the crashes.
 */
export function shouldReportWriteFailure(error) {
    return !isCallerError(error) && !isUnanswered(error);
}
/**
 * A request that never got a response -- offline, DNS, a timeout: axios's own
 * shape, which `unwrap` rethrows untouched. Nothing was decided by the server,
 * so a one-shot intent (an invitation code) is worth keeping for another try.
 */
export function isUnanswered(error) {
    const cause = error instanceof AppError ? error.cause : error;
    const axios = cause;
    return axios?.isAxiosError === true && axios.response === undefined;
}
/**
 * A request the server decided nothing about: no answer at all
 * (`isUnanswered`), or a session that ended before the request was looked at
 * -- a 401, or the `AUTH_TOKEN_INVALID` client-auth throws once its refresh
 * fails too. A one-shot intent (an invitation code) is kept for the person
 * who signs in again.
 */
export function isUndecided(error) {
    const cause = error instanceof AppError ? error.cause : error;
    const failure = cause;
    return isUnanswered(error) || (failure?.httpStatus ?? failure?.response?.status) === 401;
}
/**
 * A write the server looked at and said no to (`isCallerError`) -- the same
 * write gets the same answer, so a sheet with nothing left to do closes. Never
 * a session that ended (`isUndecided`): signing in again takes it, and the
 * write is still to be tried.
 */
export function isRefusal(error) {
    return isCallerError(error) && !isUndecided(error);
}
/** The message to show for an unknown failure, with the detail logged. */
export function userMessage(error, fallback) {
    if (error instanceof AppError)
        return error.message;
    // Reached through globalThis, and typed here rather than by adding the DOM
    // library to this package. `console` exists in Hermes, in a browser and in
    // Node, but none of the three is a dependency this package is willing to
    // declare — the whole point of it is that it runs anywhere. Neither `__DEV__`
    // nor `import.meta.env.DEV` exists in both places, so the line is
    // unconditional: an unexpected failure is worth a console entry wherever it
    // happens, and nothing here prints anything a person typed.
    const host = globalThis;
    host.console?.warn("[vozqi]", describeFailure(error));
    return fallback;
}
/**
 * What is safe to log about a failure. NEVER the error object itself: an
 * AxiosError carries `config.data` and `config.headers`, which hold whatever
 * the request sent -- a PAC's keys, a password, a token -- and the console
 * (and a Sentry breadcrumb behind it) is not where those belong.
 */
function describeFailure(error) {
    const failure = (error ?? {});
    const url = typeof failure.config?.url === "string" ? (failure.config.url.split("?")[0] ?? "") : undefined;
    return {
        name: failure.name,
        message: failure.message,
        code: failure.code,
        status: failure.response?.status,
        method: failure.config?.method,
        path: url,
    };
}
